import { useState, DragEvent } from 'react';

interface UseFileDragDropOptions {
  onDrop: (files: File | File[]) => void;
  multiple?: boolean;
  disabled?: boolean;
}

interface UseFileDragDropReturn {
  isDragging: boolean;
  dragHandlers: {
    onDragOver: (e: DragEvent<HTMLDivElement>) => void;
    onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
    onDrop: (e: DragEvent<HTMLDivElement>) => void;
  };
}

/**
 * Hook for handling file drag and drop with visual feedback
 * @param options - Configuration options for drag and drop behavior
 * @returns Object containing isDragging state and drag event handlers
 */
export function useFileDragDrop(options: UseFileDragDropOptions): UseFileDragDropReturn {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!options.disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (options.disabled) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (options.multiple) {
      options.onDrop(files);
    } else {
      options.onDrop(files[0]);
    }
  };

  return {
    isDragging,
    dragHandlers: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
